import { ChangeDetectionStrategy, Component, computed, inject, Inject, Signal } from '@angular/core';
import { MngOutComponent } from '@mng/mng-base-ui';
import { LaborProtokollService } from '../labor-protokoll.service';
import { getFormattedDateTimeStringFor, openDownloadWithFilename } from '@mng/mng-ts-helpers';
import {
  DIALOG_DATEN_INVALID_OPTIONS,
  DIALOG_DATEN_NOT_DIRTY_OPTIONS,
  InputFieldStringComponent,
  MngDialogService,
  UiGroup
} from '@mng/mng-inputs';
import {
  DIENSTLEISTUNG_CODE_FORMAT_MESSAGE,
  getErrorMessage,
  hasError,
  ILaborParameterValueHolder,
  ILaborProtokoll,
  istCodeValid,
  STATUS_INDIVIDUALKUNDE,
  STATUS_NICHT_UEBERNOMMEN,
  STATUS_UEBERNOMMEN
} from '@mng/mgs-lib';
import {
  gridColumn,
  gridFormattedStrongColumn,
  gridFormattedStyledColumn,
  MngTabulatorComponent,
  MngTabulatorViewComponent
} from '@mng/mng-tabulator';
import { ColumnDefinition } from 'tabulator-tables';
import { MGS_EDIT_ICON, MGS_PDF_ICON } from '../../../mgs-svg-icon.helper';
import { Router, RouterLink } from '@angular/router';
import { DIENSTLEISTUNG_PATH, LABOR_PROTOKOLL_PATH } from '../../../mgs-path-defs';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { Validators } from '@angular/forms';
import { MngMessageService } from '@mng/mng-message-handler';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'mng-mgs-laborprotokoll-detail',
  imports: [
    MatCard,
    MatCardContent,
    MngOutComponent,
    MngTabulatorComponent,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    RouterLink
  ],
  templateUrl: './laborprotokoll-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LaborprotokollDetailComponent extends MngTabulatorViewComponent<ILaborParameterValueHolder> {
  detail: Signal<ILaborProtokoll>;
  uebernahmeDatum = computed(() => getFormattedDateTimeStringFor(this.detail().date, 'medium'));
  readonly dialogService = inject(MngDialogService);
  readonly dialog = inject(MatDialog);

  readonly router = inject(Router);

  LABOR_PROTOKOLL_PATH = LABOR_PROTOKOLL_PATH;
  MGS_EDIT_ICON = MGS_EDIT_ICON;
  MGS_PDF_ICON = MGS_PDF_ICON;
  STATUS_UEBERNOMMEN = STATUS_UEBERNOMMEN;
  STATUS_NICHT_UEBERNOMMEN = STATUS_NICHT_UEBERNOMMEN;
  STATUS_INDIVIDUALKUNDE = STATUS_INDIVIDUALKUNDE;

  constructor(public service: LaborProtokollService) {
    super({ rawData: service.protokollDetail().untersuchungsWerte, heightOffset: '220px' });
    this.detail = service.protokollDetail;
  }

  createColumnDef(): ColumnDefinition[] {
    return [
      gridFormattedStrongColumn(gridColumn('Untersuchung', 'laborParameter.mapping.laborAttributDescription', 300)),
      gridColumn('Labor Wert', 'laborParameter.displayWert', 200),
      gridFormattedStyledColumn(
        gridColumn('Dienstleistung Wert', 'attribut.wert', 200),
        (data: ILaborParameterValueHolder) => {
          if (data.message) {
            return 'color:red';
          } else {
            return '';
          }
        }
      )
    ];
  }

  laborProtokollBearbeitung(statusWechsel: number) {
    let htmlContent: string;
    if (STATUS_UEBERNOMMEN === statusWechsel) {
      htmlContent = 'Möchten Sie die Laborwerte in die Dienstleistung übernehmen?';
    } else if (STATUS_INDIVIDUALKUNDE === statusWechsel) {
      htmlContent = 'Möchten Sie die Aktion "Laborprotokoll für einen Individualkunden kennzeichnen" durchführen?';
    } else if (STATUS_NICHT_UEBERNOMMEN === statusWechsel) {
      htmlContent = 'Möchten Sie die Aktion "Laborwerte NICHT übernehmen" durchführen?';
    } else {
      throw Error('Falscher STatus');
    }

    this.dialogService.showYesNo({ htmlContent }, async () => {
      await this.service.laborProtokollBearbeitung(statusWechsel);

      if (STATUS_UEBERNOMMEN === statusWechsel && this.detail().dienstleistungsCode) {
        void this.router.navigate(
          [DIENSTLEISTUNG_PATH, this.detail().dienstleistungsCode, 'gruppe', 'status-und-labor'],
          {
            queryParams: {
              untergruppe: 'blutuntersuchung'
            }
          }
        );
      } else {
        void this.router.navigate([LABOR_PROTOKOLL_PATH]);
      }
    });
  }

  zuordnungAendern() {
    if (!this.service.protokollZuordenbar()) {
      return;
    }

    this.dialog.open(LaborProtokollZuordnungComponent, {
      width: '600px',
      data: { protokoll: this.detail() } as ILaborProtokollZuordnungParams
    });
  }

  async pdfFuerLaborDatenErmitteln() {
    try {
      const objectUrl = await this.service.webApi.laborProtokollPdf(this.detail().id);
      openDownloadWithFilename(objectUrl.filename, objectUrl.blob);
    } catch (e) {
      console.error(e);
    }
  }
}

class LaborProtokollUiGroup extends UiGroup<Partial<ILaborProtokoll>> {
  constructor(protokoll: ILaborProtokoll) {
    super(protokoll);
    this.addField('dienstleistungsCode', protokoll.dienstleistungsCode, 'Code').withValidators(Validators.required);
  }
}

export interface ILaborProtokollZuordnungParams {
  protokoll: ILaborProtokoll;
}

@Component({
  imports: [MatDialogContent, MatDialogActions, MatButton, MatDialogClose, MatDialogTitle, InputFieldStringComponent],
  template: `
    <div matDialogTitle>Dienstleistung Zuordnung</div>

    <mat-dialog-content style="margin-top: 20px;">
      <div class="flex-column" style="padding: 10px;">
        <mng-inputfield-string
          [ngStyle]="{ width: '200px' }"
          [field]="uiGroup.field('dienstleistungsCode')"
          subscriptSizing="dynamic"
        />
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-raised-button color="accent" (click)="zuordnungAendern()">Zuordnung ändern</button>
      <button mat-raised-button color="accent" mat-dialog-close>Schliessen</button>
    </mat-dialog-actions>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LaborProtokollZuordnungComponent {
  readonly dialogService = inject(MngDialogService);
  readonly messageService = inject(MngMessageService);
  readonly service = inject(LaborProtokollService);
  uiGroup: LaborProtokollUiGroup;
  constructor(
    public dialogRef: MatDialogRef<LaborProtokollZuordnungComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ILaborProtokollZuordnungParams
  ) {
    this.uiGroup = new LaborProtokollUiGroup(data.protokoll);
  }

  close() {
    this.dialogRef.close();
  }

  async zuordnungAendern() {
    if (!this.uiGroup.control.dirty) {
      this.dialogService.showOK(DIALOG_DATEN_NOT_DIRTY_OPTIONS);
      return;
    }

    if (!this.uiGroup.isValid()) {
      this.dialogService.showOK(DIALOG_DATEN_INVALID_OPTIONS);
      return;
    }

    const updated = this.uiGroup.getUpdatedData();
    const code: string = updated.dienstleistungsCode?.trim();
    if (!istCodeValid(code)) {
      const htmlContent = `Bitte geben Sie einen gültigen Code ein! <br><br>${DIENSTLEISTUNG_CODE_FORMAT_MESSAGE}`;
      this.dialogService.showOK({ htmlContent });
      return;
    }

    if (code === this.data.protokoll.dienstleistungsCode) {
      const htmlContent = `Der eingegebene Code ist gleich dem Code der schon zugeordneten Dienstleistung! (<strong>${this.data.protokoll.dienstleistungsCode}</strong>)`;
      this.dialogService.showOK({ htmlContent });
      return;
    }

    this.dialogService.showYesNo(
      {
        htmlContent: `Möchten Sie die Zuordnung des Laborprotokoll auf die Dienstleistung mit dem Code <strong>${code}</strong> ändern?`
      },
      async () => {
        const response = await this.service.laborProtokollZuordnungAenderung(code);

        if (!hasError(response)) {
          void this.messageService.showMessage('Die Zuordnung wurde geändert');
          this.dialogRef.close();
        } else {
          this.dialogService.showOK({ htmlContent: getErrorMessage(response).message });
        }
      }
    );
  }
}
